import { defineComponent as _defineComponent } from 'vue';
import { BAlert } from "bootstrap-vue";
import Vue, { computed, onMounted, ref } from "vue";
import { browseRemoteFiles, fetchFileSources, } from "@/api/remoteFiles";
import { UrlTracker } from "@/components/DataDialog/utilities";
import { fileSourcePluginToItem, isSubPath } from "@/components/FilesDialog/utilities";
import { SELECTION_STATES, } from "@/components/SelectionDialog/selectionTypes";
import { useConfig } from "@/composables/config";
import { useFileSources } from "@/composables/fileSources";
import { errorMessageAsString } from "@/utils/simple-error";
import { USER_FILE_PREFIX } from "@/utils/upload-payload";
import { Model } from "./model";
import SelectionDialog from "@/components/SelectionDialog/SelectionDialog.vue";
export default /*#__PURE__*/ _defineComponent({
    __name: 'FilesDialog',
    props: {
        callback: { type: Function, default: () => { } },
        filterOptions: { default: undefined },
        modalStatic: { type: Boolean, default: false },
        mode: { default: "file" },
        multiple: { type: Boolean, default: false },
        requireWritable: { type: Boolean, default: false },
        selectedItem: { default: undefined },
        isOpen: { type: Boolean, default: true }
    },
    setup(__props) {
        const props = __props;
        const filesSources = useFileSources();
        const { config, isConfigLoaded } = useConfig();
        const selectionModel = ref(new Model({ multiple: props.multiple }));
        const allSelected = ref(false);
        const selectedDirectories = ref([]);
        const errorMessage = ref();
        const filter = ref();
        const items = ref([]);
        const itemsProvider = ref();
        const modalShow = ref(props.isOpen);
        const optionsShow = ref(false);
        const undoShow = ref(false);
        const hasValue = ref(false);
        const showTime = ref(true);
        const showDetails = ref(true);
        const isBusy = ref(false);
        const currentDirectory = ref();
        const showFTPHelper = ref(false);
        const selectAllIcon = ref(SELECTION_STATES.UNSELECTED);
        const urlTracker = ref(new UrlTracker(""));
        const totalItems = ref(0);
        const fields = computed(() => {
            const fields = [];
            fields.push({ key: "label" });
            if (showDetails.value) {
                fields.push({ key: "details" });
            }
            if (showTime.value) {
                fields.push({ key: "time" });
            }
            return fields;
        });
        const fileMode = computed(() => props.mode == "file");
        const okButtonDisabled = computed(() => (fileMode.value && !hasValue.value) || isBusy.value || (!fileMode.value && urlTracker.value.atRoot()));
        /** Collects selected datasets in value array **/
        function clicked(record) {
            // ignore the click during directory mode
            if (!fileMode.value) {
                return;
            }
            if (record.isLeaf) {
                // record is file
                selectSingleRecord(record);
            }
            else {
                // you cannot select entire root directory
                urlTracker.value.atRoot() ? open(record) : selectDirectoryRecursive(record);
            }
            formatRows();
        }
        function selectSingleRecord(record, selectOnly = false) {
            const selected = selectionModel.value.exists(record.id);
            if (selected) {
                unselectPath(record.url, true);
            }
            if (selectOnly) {
                if (!selected) {
                    selectionModel.value.add(record);
                }
            }
            else {
                selectionModel.value.add(record);
            }
            hasValue.value = selectionModel.value.count() > 0;
            if (props.multiple) {
                formatRows();
            }
            else {
                finalize();
            }
        }
        function unselectPath(path, unselectOnlyAboveDirectories = false, unselectId) {
            // unselect directories
            selectedDirectories.value = selectedDirectories.value.filter((directory) => {
                if (unselectId === directory.id) {
                    return false;
                }
                let matched;
                if (unselectOnlyAboveDirectories) {
                    matched = isSubPath(directory.url, path);
                }
                else {
                    // unselect all folders under or above the current path
                    matched = isSubPath(directory.url, path) || isSubPath(path, directory.url);
                }
                // filter out those that matched
                return !matched;
            });
            // unselect files
            if (!unselectOnlyAboveDirectories) {
                // unselect all files under this path
                selectionModel.value.unselectUnderPath(path);
            }
        }
        function selectDirectoryRecursive(record) {
            // if directory is `selected` or `mixed` unselect everything
            if (isDirectorySelected(record.id) || selectionModel.value.pathExists(record.url)) {
                unselectPath(record.url, false, record.id);
            }
            else {
                selectedDirectories.value.push(record);
                // look for subdirectories
                const recursive = true;
                isBusy.value = true;
                browseRemoteFiles(record.url, recursive).then((incoming) => {
                    incoming.entries.forEach((item) => {
                        // construct record
                        const subRecord = entryToRecord(item);
                        if (subRecord.isLeaf) {
                            // select file under this path
                            selectSingleRecord(subRecord, true);
                        }
                        else {
                            // select subdirectory
                            selectedDirectories.value.push(subRecord);
                        }
                    });
                    totalItems.value = incoming.totalMatches;
                    isBusy.value = false;
                });
            }
        }
        /** Add highlighting for record variations, i.e. datasets vs. libraries/collections **/
        function formatRows() {
            const getIcon = (isSelected, path) => {
                if (isSelected) {
                    return SELECTION_STATES.SELECTED;
                }
                else {
                    return selectionModel.value.pathExists(path) ? SELECTION_STATES.MIXED : SELECTION_STATES.UNSELECTED;
                }
            };
            hasValue.value = selectionModel.value.count() > 0 || selectedDirectories.value.length > 0;
            for (const item of items.value) {
                let _rowVariant = "active";
                if (item.isLeaf || !fileMode.value) {
                    _rowVariant = selectionModel.value.exists(item.id) ? "success" : "default";
                }
                // if directory
                else if (!item.isLeaf) {
                    _rowVariant = getIcon(isDirectorySelected(item.id), item.url);
                }
                Vue.set(item, "_rowVariant", _rowVariant);
            }
            allSelected.value = checkIfAllSelected();
            if (currentDirectory.value?.url) {
                selectAllIcon.value = getIcon(allSelected.value, currentDirectory.value.url);
            }
        }
        function isDirectorySelected(directoryId) {
            return selectedDirectories.value.some(({ id }) => id === directoryId);
        }
        /** check if all objects in this folders are selected **/
        function checkIfAllSelected() {
            const isAllSelected = Boolean(items.value.length) &&
                items.value.every(({ id }) => selectionModel.value.exists(id) || isDirectorySelected(id));
            if (isAllSelected && currentDirectory.value && !isDirectorySelected(currentDirectory.value.id)) {
                // if all selected, select current folder
                selectedDirectories.value.push(currentDirectory.value);
            }
            return isAllSelected;
        }
        function open(record) {
            load(record);
        }
        /** Performs server request to retrieve data records **/
        function load(record) {
            currentDirectory.value = urlTracker.value.getUrl(record);
            showFTPHelper.value = record?.url === "gxftp://";
            filter.value = undefined;
            optionsShow.value = false;
            undoShow.value = !urlTracker.value.atRoot();
            if (urlTracker.value.atRoot() || errorMessage.value) {
                itemsProvider.value = undefined;
                errorMessage.value = undefined;
                fetchFileSources(props.filterOptions)
                    .then((results) => {
                    const convertedItems = results
                        .filter((item) => !props.requireWritable || item.writable)
                        .map(fileSourcePluginToItem);
                    const sortedItems = convertedItems.sort(sortPrivateFileSourcesFirst);
                    items.value = sortedItems;
                    formatRows();
                    optionsShow.value = true;
                    showTime.value = false;
                    showDetails.value = true;
                    totalItems.value = convertedItems.length;
                })
                    .catch((error) => {
                    errorMessage.value = errorMessageAsString(error);
                });
            }
            else {
                if (!currentDirectory.value) {
                    return;
                }
                if (props.mode === "source") {
                    // In source mode, only show sources, not contents
                    items.value = [];
                    optionsShow.value = true;
                    showTime.value = false;
                    showDetails.value = false;
                    return;
                }
                if (shouldUseItemsProvider()) {
                    itemsProvider.value = (ctx) => provideItems(ctx, currentDirectory.value?.url);
                    optionsShow.value = true;
                    showTime.value = true;
                    showDetails.value = false;
                    return;
                }
                browseRemoteFiles(currentDirectory.value?.url, false, props.requireWritable)
                    .then((result) => {
                    items.value = filterByMode(result.entries).map(entryToRecord);
                    totalItems.value = result.totalMatches;
                    formatRows();
                    optionsShow.value = true;
                    showTime.value = true;
                    showDetails.value = false;
                })
                    .catch((error) => {
                    errorMessage.value = errorMessageAsString(error);
                });
            }
        }
        function isPrivateFileSource(item) {
            return item.url.startsWith(USER_FILE_PREFIX);
        }
        function sortPrivateFileSourcesFirst(a, b) {
            if (isPrivateFileSource(a) && !isPrivateFileSource(b)) {
                return -1;
            }
            if (!isPrivateFileSource(a) && isPrivateFileSource(b)) {
                return 1;
            }
            return 0;
        }
        /**
         * Check if the current file source supports server-side pagination.
         * If it does, we will use the items provider to fetch items.
         */
        function shouldUseItemsProvider() {
            if (!currentDirectory.value) {
                return false;
            }
            const fileSource = filesSources.getFileSourceByUri(currentDirectory.value.url);
            const supportsPagination = fileSource?.supports?.pagination;
            return Boolean(supportsPagination);
        }
        /**
         *  Fetches items from the server using server-side pagination and filtering.
         **/
        async function provideItems(ctx, url) {
            isBusy.value = true;
            try {
                if (!url) {
                    return [];
                }
                const limit = ctx.perPage;
                const offset = (ctx.currentPage - 1) * ctx.perPage;
                const query = ctx.filter;
                const response = await browseRemoteFiles(url, false, props.requireWritable, limit, offset, query);
                const result = response.entries.map(entryToRecord);
                totalItems.value = response.totalMatches;
                items.value = result;
                formatRows();
                return result;
            }
            catch (error) {
                errorMessage.value = errorMessageAsString(error);
                return [];
            }
            finally {
                isBusy.value = false;
            }
        }
        function filterByMode(results) {
            if (!fileMode.value) {
                // In directory mode, only show directories
                return results.filter((item) => item.class === "Directory");
            }
            return results;
        }
        function entryToRecord(entry) {
            const result = {
                id: entry.uri,
                label: entry.name,
                time: entry.class === "File" ? entry.ctime : "",
                details: entry.class === "File" ? entry.ctime : "",
                isLeaf: entry.class === "File",
                url: entry.uri,
                size: entry.class === "File" ? entry.size : 0,
            };
            return result;
        }
        /** select all files in current folder**/
        function onSelectAll() {
            if (!currentDirectory.value) {
                return;
            }
            const isUnselectAll = selectionModel.value.pathExists(currentDirectory.value.url);
            for (const item of items.value) {
                if (isUnselectAll) {
                    if (selectionModel.value.exists(item.id) || selectionModel.value.pathExists(item.id)) {
                        item.isLeaf ? selectionModel.value.add(item) : selectDirectoryRecursive(item);
                    }
                }
                else {
                    item.isLeaf ? selectionModel.value.add(item) : selectDirectoryRecursive(item);
                }
            }
            if (!isUnselectAll && items.value.length !== 0) {
                selectedDirectories.value.push(currentDirectory.value);
            }
            else if (isDirectorySelected(currentDirectory.value.id)) {
                selectDirectoryRecursive(currentDirectory.value);
            }
            hasValue.value = selectionModel.value.count() > 0;
            formatRows();
        }
        /** Called when selection is complete, values are formatted and parsed to external callback **/
        function finalize() {
            const results = selectionModel.value.finalize();
            modalShow.value = false;
            props.callback(results);
        }
        function onOk() {
            if (!fileMode.value && currentDirectory.value) {
                selectSingleRecord(currentDirectory.value);
            }
            finalize();
        }
        onMounted(() => {
            load(props.selectedItem);
        });
        return { __sfc: true, filesSources, props, config, isConfigLoaded, selectionModel, allSelected, selectedDirectories, errorMessage, filter, items, itemsProvider, modalShow, optionsShow, undoShow, hasValue, showTime, showDetails, isBusy, currentDirectory, showFTPHelper, selectAllIcon, urlTracker, totalItems, fields, fileMode, okButtonDisabled, clicked, selectSingleRecord, unselectPath, selectDirectoryRecursive, formatRows, isDirectorySelected, checkIfAllSelected, open, load, isPrivateFileSource, sortPrivateFileSourcesFirst, shouldUseItemsProvider, provideItems, filterByMode, entryToRecord, onSelectAll, finalize, onOk, BAlert, SelectionDialog };
    }
});
